import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { toast } from 'sonner'

import { labApi, type LabGameSummary } from '../api/labApi'

interface LabSessionControlProps {
  tableId: string
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function LabSessionControl({ tableId, isOpen, onOpenChange }: LabSessionControlProps) {
  const [games, setGames] = useState<LabGameSummary[]>([])
  const [selectedSlug, setSelectedSlug] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setLoading(true)
    labApi
      .listGames()
      .then((list) => {
        if (cancelled) return
        setGames(list)
        if (list.length > 0 && !selectedSlug) setSelectedSlug(list[0].slug)
      })
      .catch((err) => {
        console.error('[LAB] erro ao listar aulas:', err)
        toast.error('Não foi possível carregar a lista de aulas.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  const handleStart = async () => {
    if (!selectedSlug) {
      toast.error('Escolha uma aula para iniciar.')
      return
    }

    setSubmitting(true)
    try {
      const created = await labApi.createSession(tableId, selectedSlug)
      await labApi.startSession(created.id)
      toast.success('Aula iniciada!')
      onOpenChange(false)
    } catch (err: unknown) {
      console.error('[LAB] erro ao iniciar aula:', err)
      const msg = err instanceof Error ? err.message : String(err)
      if (msg.includes('409')) {
        toast.error('Já existe uma aula ativa nesta mesa. Encerre antes de iniciar outra.')
      } else {
        toast.error('Não foi possível iniciar a aula.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="bg-game-secondary border-game-primary max-w-md">
        <DialogHeader>
          <DialogTitle className="text-game-primary">Iniciar Aula Interativa</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Label className="text-game-primary">Escolha a aula</Label>

          {loading ? (
            <p className="text-sm text-game-muted">Carregando aulas…</p>
          ) : games.length === 0 ? (
            <p className="text-sm text-game-muted">Nenhuma aula disponível.</p>
          ) : (
            <RadioGroup
              value={selectedSlug}
              onValueChange={setSelectedSlug}
              className="space-y-2"
            >
              {games.map((game) => (
                <div
                  key={game.slug}
                  className="flex items-start space-x-3 p-3 rounded-lg bg-game-dark border border-game-hover hover:border-emerald-500/50 transition-colors"
                >
                  <RadioGroupItem
                    value={game.slug}
                    id={`game-${game.slug}`}
                    className="border-game-hover text-emerald-500 mt-1"
                  />
                  <Label htmlFor={`game-${game.slug}`} className="flex-1 cursor-pointer">
                    <div className="text-game-primary font-medium">{game.title}</div>
                    {game.subject && (
                      <div className="text-xs text-game-muted mt-0.5">{game.subject}</div>
                    )}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}

          <p className="text-[11px] text-zinc-500 italic">
            A aula abrirá em tela cheia para todos os alunos da mesa. Alunos que entrarem na
            mesa depois do início não conseguirão participar desta sessão.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-game-muted">
            Cancelar
          </Button>
          <Button
            onClick={handleStart}
            disabled={submitting || loading || !selectedSlug}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {submitting ? 'Iniciando…' : 'Iniciar para Todos'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
